import type {EmptyStateProps} from 'shells/card/card-widget/card-widget';
// @ts-expect-error - imported from .js/.jsx file
import {CheckmarkIcon} from 'shells/icon/svg/checkmark-icon';
import {CompanyRegularIcon} from 'shells/icon/svg/company-regular-icon';
import {PersonRoundRegularIcon} from 'shells/icon/svg/person-round-regular-icon';
import type {SelectMenuOption} from 'shells/select-menu/select-menu';
import type {Tab} from 'shells/tabs/page-tabs/page-tabs';

import type {PeepRelatedPeepSortType} from 'nutshell-graphql-types';

import type {PeepType} from '../../types';

import {SORT_OPTIONS} from './consts';
import type {RelatedPeepsTab} from './types';

export function getTabProps(
    selectedTab: RelatedPeepsTab,
    handleTabChange: (tab: RelatedPeepsTab) => void,
    currentCount: number,
    formerCount: number
): Tab[] {
    if (currentCount < 6 && formerCount === 0) {
        return [];
    }

    // These ids match the RelatedPeepsTab type in types.ts
    return [
        {
            id: 'current',
            label: 'Current',
            isActive: selectedTab === 'current',
            count: String(currentCount),
            onClick: () => handleTabChange('current'),
        },
        {
            id: 'former',
            label: 'Former',
            isActive: selectedTab === 'former',
            count: String(formerCount),
            onClick: () => handleTabChange('former'),
        },
    ];
}

export function getFilterString(
    currentSortBy: PeepRelatedPeepSortType | null,
    formerSortBy: PeepRelatedPeepSortType | null,
    selectedTab: RelatedPeepsTab,
    currentUnfilteredCount: number,
    formerUnfilteredCount: number,
    filteredCount: number,
    peepType: PeepType
): string {
    const selectedSortBy = selectedTab === 'current' ? currentSortBy : formerSortBy;

    return `Showing ${filteredCount} of ${
        selectedTab === 'current' ? currentUnfilteredCount : formerUnfilteredCount
    } ${selectedTab} ${peepType === 'contact' ? 'contacts' : 'companies'} • Sorted by: ${
        selectedSortBy !== null
            ? SORT_OPTIONS.find((option) => option.value === selectedSortBy)?.label
            : 'Default'
    }`;
}

export function getSortByOptions(
    currentSortBy: PeepRelatedPeepSortType | null,
    formerSortBy: PeepRelatedPeepSortType | null,
    selectedTab: RelatedPeepsTab,
    handleSortByChange: (optionValue: PeepRelatedPeepSortType | null) => void
): SelectMenuOption[] {
    return SORT_OPTIONS.map((option) => ({
        ...option,
        handleSelect: () => handleSortByChange(option.value),
        iconPosition: 'right',
        icon:
            (option.value === currentSortBy && selectedTab === 'current') ||
            (option.value === formerSortBy && selectedTab === 'former') ? (
                <CheckmarkIcon size={12} wrapWithDiv={false} />
            ) : undefined,
    }));
}

export function getIsEmpty(
    selectedTab: RelatedPeepsTab,
    currentCount: number,
    formerCount: number,
    isCreatingAccount: boolean,
    isRefetching: boolean,
    isShowingFilter: boolean
) {
    return (
        ((selectedTab === 'current' && currentCount === 0) ||
            (selectedTab === 'former' && formerCount === 0)) &&
        !isCreatingAccount &&
        !isRefetching &&
        !isShowingFilter
    );
}

export function getPeepEmptyStateProps(entityType: PeepType, isEmpty: boolean): EmptyStateProps {
    return {
        isEmpty: isEmpty,
        text: entityType === 'contact' ? 'No related people' : 'No related companies',
        icon: entityType === 'contact' ? PersonRoundRegularIcon : CompanyRegularIcon,
        iconSize: 22,
    };
}
